import { Card, CardContent, CardActions, Tooltip } from '@mui/material';
import { Device } from 'types';
import {
    FormProvider,
    FormSelect,
    FormSubmitButton,
    FormSubmitButtonProps,
} from 'components/form';
import * as yup from 'yup';
import { FormSelectOption } from 'components/form/types';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { activateSnackbar } from 'store/features/ui';
import Save from '@mui/icons-material/Save';
import styles from './DeviceFrequency.module.scss';
import { HelpOutline } from '@mui/icons-material';
import { DeviceFrequencyVirtualDeviceCard } from './DeviceFrequencyVirtualDeviceCard/DeviceFrequencyVirtualDeviceCard';
import { useLazyPatchUpdateDeviceQuery } from 'store/services';

export interface DeviceFrequencyProps {
    device: Device;
}

type DeviceFrequencyInput = {
    tcov: Device['tcov'];
    t_int: Device['t_int'];
    r_int: Device['r_int'];
    ftt: Device['ftt'];
};

const deviceFrequencySchema = yup
    .object<{ [k in keyof DeviceFrequencyInput]: yup.AnySchema }>({
        // SECTION details
        tcov: yup.number().required(),
        t_int: yup.number().required(),
        r_int: yup.number().required(),
        ftt: yup.number().required(),
    })
    .required();

const tooltips = {
    tcov: 'The measured change in tank level % since the last transmitted reading that will cause the device to immediately transmit the most current reading without waiting for the full Reporting Interval to expire.',
    r_int: 'The number of hours between comparison to the Change of Tank Level threshold measurements. For example, Reading Interval of 1 hour, will check the tank level 24 times per day, but only send a tank level update when a change is greater than the Change of Tank Level threshold.',
    t_int: 'The number of hours between readings being updated in the portal. For example, Reporting Interval of 12 hours, will send two readings per day.',
    ftt: 'The monitor will send a reading by this time. Only one reading will be sent every 24 hours.',
};

export const DeviceFrequency = ({ device }: DeviceFrequencyProps) => {
    const dispatch = useDispatch();
    const [isSaving, setIsSaving] = useState(false);
    const [updateDeviceQuery] = useLazyPatchUpdateDeviceQuery();

    const tcovPercentages: FormSelectOption[] = [
        { value: -1, name: 'Default' },
        { value: 5, name: '5%' },
        { value: 10, name: '10%' },
        { value: 15, name: '15%' },
        { value: 20, name: '20%' },
        { value: 25, name: '25%' },
        { value: 30, name: '30%' },
        { value: 35, name: '35%' },
        { value: 40, name: '40%' },
        { value: 55, name: '55%' },
        { value: 60, name: '60%' },
    ];

    const rIntTimeOptions: FormSelectOption[] = [
        { value: 1 * 60 * 60, name: '1 Hour' },
        { value: 2 * 60 * 60, name: '2 Hours' },
        { value: 3 * 60 * 60, name: '3 Hours' },
        { value: 4 * 60 * 60, name: '4 Hours' },
        { value: 6 * 60 * 60, name: '6 Hours (default)' },
        { value: 12 * 60 * 60, name: '12 Hours' },
        { value: 24 * 60 * 60, name: '24 Hours' },
        { value: 48 * 60 * 60, name: '48 Hours' },
        { value: 72 * 60 * 60, name: '72 Hours' },
        { value: 96 * 60 * 60, name: '96 Hours' },
    ];

    const tIntTimeOptions: FormSelectOption[] = [
        { value: 1 * 60 * 60, name: '1 Hour' },
        { value: 2 * 60 * 60, name: '2 Hours' },
        { value: 3 * 60 * 60, name: '3 Hours' },
        { value: 4 * 60 * 60, name: '4 Hours' },
        { value: 6 * 60 * 60, name: '6 Hours' },
        { value: 12 * 60 * 60, name: '12 Hours' },
        { value: 24 * 60 * 60, name: '24 Hours (default)' },
        { value: 48 * 60 * 60, name: '48 Hours' },
        { value: 72 * 60 * 60, name: '72 Hours' },
        { value: 96 * 60 * 60, name: '96 Hours' },
    ];

    const fttTimeOptions: FormSelectOption[] = useMemo(() => {
        let currentTime = new Date();

        return [
            { value: -1, name: 'Default' },
            ...Array.from({ length: 24 }, () => {
                const value = currentTime.getUTCHours() * 3600;
                const out = {
                    name: new Date(
                        currentTime.setMinutes(0, 0, 0)
                    ).toLocaleTimeString('en-us', {
                        hour: 'numeric',
                        minute: '2-digit',
                    }),
                    value,
                };
                currentTime = new Date(currentTime.getTime() + 60 * 60 * 1000);
                return out;
            }),
        ];
    }, []);

    const onValidHandler: FormSubmitButtonProps<DeviceFrequencyInput>['onValidHandler'] =
        (data) => {
            setIsSaving(true);
            const { tcov, r_int, t_int, ftt } = data;

            updateDeviceQuery({
                deviceId: device.deviceId,
                updatedFields: { tcov, r_int, t_int, ftt },
            })
                .then((result) => {
                    if (result.isSuccess && result.data) {
                        setIsSaving(false);
                        dispatch(
                            activateSnackbar({
                                message:
                                    'Frequency config updated. Changes take effect at next reading.',
                                severity: 'success',
                            })
                        );
                    }
                })
                .catch(() => {
                    setIsSaving(false);
                });
        };

    const onErrorHandler: FormSubmitButtonProps<DeviceFrequencyInput>['onErrorHandler'] =
        (data) => {
            setIsSaving(false);
            return data;
        };

    const defaultValues: DeviceFrequencyInput = useMemo(
        () => ({
            tcov: device.tcov ?? -1,
            r_int: device.r_int ?? 6 * 60 * 60,
            t_int: device.t_int ?? 24 * 60 * 60,
            ftt: device.ftt ?? -1,
        }),
        [device]
    );

    return (
        <div data-testid="DeviceFrequency">
            {!device.virtual ? (
                <FormProvider
                    defaultValues={defaultValues}
                    validationSchema={deviceFrequencySchema}
                >
                    <Card
                        className={styles.card}
                        data-testid="DeviceFrequencySettingsForm"
                    >
                        <CardContent className={styles.cardContent}>
                            <div className={styles.inputContainer}>
                                <FormSelect
                                    field="tcov"
                                    options={tcovPercentages}
                                    label="Change of Tank Level Since Last Transmission"
                                />
                                <Tooltip
                                    title={tooltips['tcov']}
                                    enterTouchDelay={0}
                                    className={styles.tooltip}
                                    placement="bottom"
                                    arrow
                                >
                                    <HelpOutline
                                        color="action"
                                        fontSize="small"
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.inputContainer}>
                                <FormSelect
                                    field="r_int"
                                    options={rIntTimeOptions}
                                    label="Reading Interval"
                                    customValueFormatter={(value) =>
                                        `${value / 60 / 60} Hours`
                                    }
                                />
                                <Tooltip
                                    title={tooltips['r_int']}
                                    enterTouchDelay={0}
                                    placement="bottom"
                                    arrow
                                >
                                    <HelpOutline
                                        color="action"
                                        fontSize="small"
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.inputContainer}>
                                <FormSelect
                                    field="t_int"
                                    options={tIntTimeOptions}
                                    label="Reporting Interval"
                                    customValueFormatter={(value) =>
                                        `${value / 60 / 60} Hours`
                                    }
                                />
                                <Tooltip
                                    title={tooltips['t_int']}
                                    enterTouchDelay={0}
                                    className={styles.tooltip}
                                    placement="bottom"
                                    arrow
                                >
                                    <HelpOutline
                                        color="action"
                                        fontSize="small"
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.inputContainer}>
                                <FormSelect
                                    field="ftt"
                                    options={fttTimeOptions}
                                    label="Fixed Reporting Time"
                                    customValueFormatter={(value) =>
                                        `${value / 60 / 60} Hours`
                                    }
                                />
                                <Tooltip
                                    title={tooltips['ftt']}
                                    enterTouchDelay={0}
                                    className={styles.tooltip}
                                    placement="bottom"
                                    arrow
                                >
                                    <HelpOutline
                                        color="action"
                                        fontSize="small"
                                    />
                                </Tooltip>
                            </div>
                        </CardContent>
                        <CardActions className={styles.actionButtonContainer}>
                            <FormSubmitButton
                                onValidHandler={onValidHandler}
                                onErrorHandler={onErrorHandler}
                                buttonLabel="Save"
                                startIcon={<Save />}
                                isLoading={isSaving}
                            />
                        </CardActions>
                    </Card>
                </FormProvider>
            ) : (
                <DeviceFrequencyVirtualDeviceCard device={device} />
            )}
        </div>
    );
};
